import { defineDomSchema } from '@/utils/dom-schema'

import type { Column, ColumnId } from '~shared/rundown/column'
import type { Cue, CueId } from '~shared/rundown/cue'
/**
 * Unique identifier for a cell, composed from the parent Cue ID and parent
 * Column ID. This key identifies cells even when sparse and actually `cell` is
 * undefined.
 *
 * @remarks
 *   Autogenerated Firestore document ID's are alphanumeric only.
 *   But technically custom document ID's _can contain_ `_` which
 *   would break the parsing of these keys. Not likely at the moment.
 */
export type CellKey = `cell_${CueId}_${ColumnId}` & {
  readonly __brand: 'CellKey'
}

export function makeCellKey(cueId: Cue['id'], columnId: Column['id']): CellKey {
  return `cell_${cueId}_${columnId}` as CellKey
}

export function parseCellKey(key: CellKey): {
  cueId: CueId
  columnId: ColumnId
} {
  if (!key) throw Error('Invalid CellKey')
  const [type, cueId, columnId] = key.split('_')

  if (type !== 'cell' || !cueId || !columnId) {
    throw new Error(`Invalid cellKey: ${key}`)
  }

  return { cueId: cueId as CueId, columnId: columnId as ColumnId }
}

/**
 * Allowed custom properties on a Cell's DOM element (`<CellFrame/>`)
 */
export type CellDomSchema = {
  'data-cell-key': CellKey
  'aria-selected': boolean
}

/**
 * Helper for querying and manipulating cell DOM elements.
 */
export const CellDomUtils = defineDomSchema<CellDomSchema, HTMLDivElement>()
